import { useParams, useSearchParams } from 'react-router-dom';
import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import OrderManager from '../services/api/OrderManager';
import useAlert from '../hooks/utility/useAlert';
import { useTranslation } from 'react-i18next';
import { Order } from '../services/exports/Interfaces';
import useFocus from '../hooks/utility/useFocus';
import usePusher from '../hooks/global/usePusher';
import { orderTrackingChannel } from '../services/pusher/Channels';
import {
  COUPON_TYPES,
  ORDER_METHODS,
  ORDER_PAYMENT_STATUSES,
  PUSHER_EVENTS,
  PaymentMethod,
  REDUX_STORE_SLICES,
  SCREENS,
} from '../services/exports/Constants';
import LoadingWithText from '../components-library/loading/LoadingWithText';
import Section from '../components-library/checkout/sections/Section';
import OrderBasketItem from '../components-library/basket/order-basket-item';
import useScreenType from '../hooks/utility/useScreenType';
import BackIcon from '../assets/logo/BackIcon';
import useRouter from '../hooks/navigation/useRouter';
import useTheme from '../hooks/ui/useTheme';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../store/types';
import DeliveryTracker from '../components-library/delivery/DeliveryTracker';
import useOrderFlow from '../hooks/order/useOrderFlow';
import PaymentDetails from '../components-library/payment/payment-methods/PaymentDetails';
import useDeepRef from '../hooks/utility/useDeepRef';
import AddressHelpers from '../services/helpers/AddressHelpers';
import PickupInstruction from '../components-library/order-type/PickupInstruction';
import CompanyDescription from '../components-library/company/CompanyDescription';
import LoadingPage from '../components-library/loading/LoadingPage';
import CashbackEarnedModal from '../modals/CashbackEarnedModal';
import RewardsUnlockedModal from '../modals/RewardsUnlockedModal';
import PointsEarnedModal from '../modals/PointsEarnedModal';
import collect from 'collect.js';
import useUrlParams from '../hooks/utility/useUrlParams';
import useBasket from '../hooks/basket/useBasket';
import OrderPaymentBreakdown from '../components-library/order/OrderPaymentBreakdown';
import OrderBasketSummary from '../components-library/order/OrderBasketSummary';
import OrderRating from '../components-library/order/OrderRating';
import useAuth from '../hooks/global/useAuth';
import ClaimOrderCashback from '../components-library/cashback/ClaimOrderCashback';
import classnames from 'classnames';
import { CompleteBasketItem } from '../providers/BasketProvider';
import ProfileManager from '../services/api/ProfileManager';
import useAppMethods from '../hooks/utility/useAppMethods';
import usePaymentFlow from '../hooks/payment/usePaymentFlow';
import PaymentManager from '../services/api/PaymentManager';
import useRewardAccount from '../hooks/loyalty/useRewardAccount';
import useLoyaltyProgram from '../hooks/loyalty/useLoyaltyProgram';
import useCashbackProgram from '../hooks/cashback/useCashbackProgram';
import useAnalytics from '../hooks/analytics/useAnalytics';
import PromotionManager from '../services/api/PromotionManager';
import toFlatArray from '../utils/to-flat.util';
import useMobileApp from '../hooks/mobile-app/useMobileApp';
import OrderTracker from '../features/orders/components/OrderTracker/OrderTracker';
import OrderPlacedIcon from '../modules/mobile/assets/icons/OrderPlacedIcon';
import { Routes } from '../modules/mobile/exports/Enums';
import FixedPageLayout from '../components-library/layouts/FixedPageLayout';
import useStore from '../hooks/store/useStore';
import PageHeader from '../components-library/headers/PageHeader';

export enum RedirectStatus {
  succeeded = 'succeeded',
  failed = 'failed',
}

export interface IOrderDetailsPage {
  uuid?: string;
  redirect_status?: RedirectStatus;
  onChangePaymentStatus?: (status: ORDER_PAYMENT_STATUSES) => void;
  closeModal?: () => void;
}

const Content = ({ uuid, redirect_status, onChangePaymentStatus, closeModal }: IOrderDetailsPage): JSX.Element => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:OrderDetailsScreen' });
  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const alert = useAlert();
  const { isLoggedIn } = useAuth();
  const { toggleInfoModal } = useAppMethods();
  const { isConnected, connect, disconnect } = usePusher();
  const { isDesktop } = useScreenType();
  const { isMobileApp } = useMobileApp();
  const { navigate } = useRouter();
  const { clearBasket } = useBasket();
  const { addOngoingOrder, resetOrder } = useOrderFlow();
  const [searchParams, setSearchParams] = useSearchParams();
  const { trackCompletePurchase } = useAnalytics();
  const { hasCashbackProgram } = useCashbackProgram();
  const { hasLoyaltyPromotion } = useLoyaltyProgram();
  const { balance, availableReward, nextReward } = useRewardAccount();
  const { selectedPaymentMethod, paymentOptions, findSavedPaymentMethod } = usePaymentFlow();

  const [paymentStatus, setPaymentStatus] = useState(null);
  const [order, setOrder] = useState<Order>(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [showCashbackEarnedModal, setShowCashbackEarnedModal] = useState(false);
  const [showPointsEarnedModal, setShowPointsEarnedModal] = useState(false);
  const [showRewardsUnlockedModal, setShowRewardsUnlockedModal] = useState(false);

  const orderRef = useDeepRef(order);
  const paymentStatusRef = useDeepRef(paymentStatus);
  const loadingOrderRef = useDeepRef(loadingOrder);
  const balanceRef = useDeepRef(balance);
  const availableRewardRef = useDeepRef(availableReward);

  // TODO: fix type
  const completeBasketItems = useMemo(() => {
    return (
      order &&
      order.line_items.map(
        (item) =>
          ({
            ...item,
            entry: item,
            modifiers: toFlatArray(item.modifiers, 'modifiers').map((entry) => ({ ...entry, entry })),
          }) as unknown as CompleteBasketItem,
      )
    );
  }, [order?.line_items]);

  const savedPaymentMethod = useMemo(
    () => findSavedPaymentMethod(selectedPaymentMethod),
    [selectedPaymentMethod, paymentOptions],
  );

  useFocus(
    () => initData(),
    () => null,
    true,
  );

  useEffect(() => {
    onMount();

    return () => disconnect();
  }, []);

  useEffect(() => {
    loadOrderInfo();
  }, [paymentStatus]);

  useEffect(() => {
    onChangePaymentStatus?.(paymentStatus);
  }, [paymentStatus]);

  async function onMount() {
    loadOrderPaymentStatus();
    setTimeout(() => loadOrderPaymentStatus(), 3000);
    initData();

    if (redirect_status === RedirectStatus.succeeded) {
      resetOrder();
      clearBasket();
    }

    if (redirect_status === RedirectStatus.failed) {
      toggleInfoModal({
        openModal: true,
        title: t('error_messages.payment_failed'),
        primaryButtonTitle: t('buttons.back_to_checkout'),
        onConfirm: () => {
          if (isMobileApp) {
            closeModal();
            navigate({
              mobile: {
                path: Routes.Checkout,
              },
            });
            return;
          }
          navigate({
            web: {
              to: SCREENS.CHECKOUT,
            },
          });
        },
      });
    }
  }

  async function initData() {
    loadOrderInfo();

    if (!isConnected()) {
      return connectPusher();
    }
  }

  async function connectPusher() {
    const pusher = await connect();
    pusher
      .subscribe(orderTrackingChannel(uuid))
      .bind(PUSHER_EVENTS.ORDER_PAID, orderPaidHandler)
      .bind(PUSHER_EVENTS.ORDER_UPDATED, orderUpdatedHandler);
  }

  function orderPaidHandler(data: Order) {
    if (
      isLoggedIn &&
      (selectedPaymentMethod === PaymentMethod.Card || selectedPaymentMethod === PaymentMethod.Paypal) &&
      !savedPaymentMethod
    ) {
      PaymentManager.getPaymentOptions(company?.id);
    }

    orderUpdatedHandler(data);
  }

  function orderUpdatedHandler(data: Order) {
    if (data.payment_status === ORDER_PAYMENT_STATUSES.PENDING) {
      return;
    }

    setPaymentStatus(data.payment_status);
    loadOrderInfo();
  }

  async function loadOrderPaymentStatus() {
    if (orderRef.current) {
      return;
    }

    const { success, response } = await OrderManager.getOrderPaymentStatus(uuid);

    if (!success) {
      return alert.error({
        description: t('error_messages.failed_to_load'),
      });
    }

    setPaymentStatus(response.data.data.payment_status);
  }

  const toggleCashbackEarnedModal = (show: boolean) => {
    if (!redirect_status) {
      return;
    }

    setShowCashbackEarnedModal(show);
  };

  async function loadOrderInfo() {
    const successHandler = (data: Order) => {
      redirect_status &&
        !orderRef.current &&
        data.cashback_account_operations?.length !== 0 &&
        hasCashbackProgram &&
        ProfileManager.getCashbackAccount(company?.id).then(() => toggleCashbackEarnedModal(true));
      redirect_status &&
        !orderRef.current &&
        isLoggedIn &&
        data.reward_account_operations?.length !== 0 &&
        hasLoyaltyPromotion &&
        ProfileManager.getRewardAccount(company?.id).then(() => toggleRewardsModal(data));

      setOrder(data);
      addOngoingOrder(data);

      if (!orderRef.current && redirect_status === RedirectStatus.succeeded) {
        searchParams.delete('redirect_status');
        setSearchParams(searchParams);

        trackCompletePurchase(data.payment, data);
        isLoggedIn && data.coupon_type === COUPON_TYPES.PROMO_CODE && PromotionManager.getPromoCodes(company?.id);
      }
    };
    const errorHandler = () => alert.error({ description: t('error_messages.failed_to_load') });

    if (
      paymentStatusRef.current === null ||
      paymentStatusRef.current === ORDER_PAYMENT_STATUSES.PENDING ||
      loadingOrderRef.current
    ) {
      return;
    }

    setLoadingOrder(true);
    const { response, success } = await OrderManager.getOrder(uuid);
    setLoadingOrder(false);

    if (success) {
      return successHandler(response.data.data);
    }

    return errorHandler();
  }

  function toggleRewardsModal(data: Order): void {
    if (!redirect_status) {
      return;
    }

    if (
      availableRewardRef.current &&
      balanceRef.current - +collect(data.reward_account_operations).where('value', '>', 0).sum('value') <=
        availableRewardRef.current.cost
    ) {
      return setShowRewardsUnlockedModal(true);
    }

    return setShowPointsEarnedModal(Boolean(availableReward || nextReward));
  }

  const onCashbackClaimed = () => {
    setOrder((current) => ({
      ...current,
      cashback_account_operations: collect(current.cashback_account_operations)
        .map((item) => ({
          ...item,
          is_claimed: true,
        }))
        .toArray(),
    }));

    setTimeout(() => toggleCashbackEarnedModal(true), 5);
  };

  const config: IContent = {
    paymentStatus,
    order: orderRef.current,
    completeBasketItems,
    onCashbackClaimed,
    showCashbackEarnedModal,
    showPointsEarnedModal,
    showRewardsUnlockedModal,
    setShowCashbackEarnedModal,
    setShowPointsEarnedModal,
    setShowRewardsUnlockedModal,
    closeModal,
  };

  if (!isDesktop) {
    return (
      <Content.PageLoading {...config}>
        <FixedPageLayout
          header={
            !isMobileApp && (
              <PageHeader.Back
                onClick={() =>
                  navigate({
                    web: {
                      to: SCREENS.HOME_SCREEN,
                    },
                  })
                }
                title={t('buttons.back_to_menu')}
              />
            )
          }
          footer={
            orderRef.current && (
              <ClaimOrderCashback
                order={order}
                onCashbackClaimed={onCashbackClaimed}
                classes={{
                  container: 'p-lg sticky bottom-0 w-full bg-white border-t',
                  button: '!w-full',
                }}
              />
            )
          }
        >
          <Content.Mobile {...config} />
        </FixedPageLayout>
      </Content.PageLoading>
    );
  }

  return (
    <Content.PageLoading {...config}>
      <div className="h-full relative">
        <div className="sticky top-0 z-50 bg-white">
          <PageHeader />
        </div>
        <div className="flex flex-row layout-container relative items-stretch">
          <Content.LeftColumn {...config} />
          <Content.RightColumn {...config} />
        </div>
      </div>
    </Content.PageLoading>
  );
};

const OrderDetailsScreen = (mobileProps: IOrderDetailsPage): JSX.Element => {
  const params = useParams();
  const search = useUrlParams();

  const webProps = {
    ...params,
    ...search,
  };

  return <Content {...(mobileProps.uuid ? mobileProps : webProps)} />;
};

export default OrderDetailsScreen;

interface IContent {
  paymentStatus: ORDER_PAYMENT_STATUSES;
  order: Order;
  completeBasketItems: CompleteBasketItem[];
  onCashbackClaimed: () => void;
  showCashbackEarnedModal: boolean;
  showPointsEarnedModal: boolean;
  showRewardsUnlockedModal: boolean;
  setShowCashbackEarnedModal: (show: boolean) => void;
  setShowPointsEarnedModal: (show: boolean) => void;
  setShowRewardsUnlockedModal: (show: boolean) => void;
  closeModal: () => void;
}

Content.LeftColumn = (props: IContent) => {
  const {
    order,
    paymentStatus,
    onCashbackClaimed,
    showCashbackEarnedModal,
    showPointsEarnedModal,
    showRewardsUnlockedModal,
    setShowCashbackEarnedModal,
    setShowPointsEarnedModal,
    setShowRewardsUnlockedModal,
  } = props;
  const { t } = useTranslation(null, { keyPrefix: 'Pages:OrderDetailsScreen' });
  const theme = useTheme();
  const { navigate } = useRouter();

  if (!(paymentStatus === ORDER_PAYMENT_STATUSES.PAID || paymentStatus === ORDER_PAYMENT_STATUSES.REFUNDED)) {
    return null;
  }

  if (!order) {
    return null;
  }

  return (
    <>
      <div className="lg:py-lg py-md space-y-md !pb-[100px] px-lg lg:pr-[100px] h-full">
        <button
          onClick={() =>
            navigate({
              web: {
                to: SCREENS.HOME_SCREEN,
              },
            })
          }
          className="flex items-center w-full text-sm text-gray-700 lg:w-fit"
        >
          <BackIcon className="h-3 !w-3 mr-xs" color={theme.color.gray['700']} />
          {t('buttons.back_to_menu')}
        </button>
        <Content.Instructions {...props} />
        <Content.CompanyDescription />
        <div className="mt-md p-md border rounded-lg">
          <h6>{t('labels.order_details')}</h6>
          <ClaimOrderCashback order={order} onCashbackClaimed={onCashbackClaimed} classes={{ container: 'mt-md' }} />
          <OrderRating order={order} classes={{ container: 'mt-md' }} />
          <Content.OrderSummary {...props} />
        </div>
      </div>

      <CashbackEarnedModal
        open={showCashbackEarnedModal}
        order={order}
        onCashbackClaimed={onCashbackClaimed}
        toggleModal={() => setShowCashbackEarnedModal(false)}
      />
      <PointsEarnedModal open={showPointsEarnedModal} toggleModal={() => setShowPointsEarnedModal(false)} />
      <RewardsUnlockedModal open={showRewardsUnlockedModal} toggleModal={() => setShowRewardsUnlockedModal(false)} />
    </>
  );
};

Content.RightColumn = ({ order, completeBasketItems }: IContent) => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:OrderDetailsScreen' });

  return (
    <div className="bg-gray-600 rounded-t-lg p-md pb-[50px] mt-md w-[400px] shrink-0">
      <div className="h-fit space-y-md rounded-lg sticky top-0 flex flex-col overflow-y-scroll">
        <div className="bg-white rounded-lg">
          <OrderTracker order={order} />
        </div>
        <div className="bg-white rounded-lg">
          <Section show title={t('labels.order_summary')} className="bg-white !p-md rounded-lg mb-md">
            <OrderPaymentBreakdown order={order} className="mt-md" />
          </Section>
        </div>
        {completeBasketItems.map((item: CompleteBasketItem, index: number) => (
          <div
            className={classnames({
              'mb-md': index !== completeBasketItems.length - 1,
            })}
            key={`line-item-${index}`}
          >
            <OrderBasketItem coupon_type={order?.coupon_type} data={item} index={index} />
          </div>
        ))}
      </div>
    </div>
  );
};

Content.Mobile = (props: IContent) => {
  const {
    order,
    paymentStatus,
    completeBasketItems,
    onCashbackClaimed,
    showCashbackEarnedModal,
    showPointsEarnedModal,
    showRewardsUnlockedModal,
    setShowCashbackEarnedModal,
    setShowPointsEarnedModal,
    setShowRewardsUnlockedModal,
    closeModal,
  } = props;
  const { t } = useTranslation(null, { keyPrefix: 'Pages:OrderDetailsScreen' });
  const { isMobileApp } = useMobileApp();

  if (!(paymentStatus === ORDER_PAYMENT_STATUSES.PAID || paymentStatus === ORDER_PAYMENT_STATUSES.REFUNDED)) {
    return null;
  }

  if (!order) {
    return null;
  }

  return (
    <>
      <div className="w-full">
        <div
          className={classnames('space-y-md px-md pb-[130px] w-full', {
            'pt-none': isMobileApp,
          })}
        >
          <OrderBasketSummary
            className={classnames('-mx-md', {
              'bg-gray-500': isMobileApp,
            })}
            items={completeBasketItems}
            order={order}
            hasDeliveredBy
            extra={
              <OrderPaymentBreakdown order={order} className="border-t border-black pt-md font-gray-700 font-medium" />
            }
          />
          <Content.Instructions {...props} />
          {!isMobileApp && <OrderTracker order={order} className="shadow-none !border border-t py-md rounded-lg" />}
          <Content.CompanyDescription />
          <div className="mt-md p-md border rounded-lg">
            <h6>{t('labels.order_details')}</h6>
            <Content.OrderSummary {...props} />
          </div>
          <OrderRating
            order={order}
            classes={{
              container: 'p-lg text-center border rounded-lg',
              wrapper: 'mx-auto',
            }}
            onSelected={closeModal}
          />
        </div>
        <CashbackEarnedModal
          open={showCashbackEarnedModal}
          order={order}
          onCashbackClaimed={onCashbackClaimed}
          toggleModal={() => setShowCashbackEarnedModal(false)}
        />
        <PointsEarnedModal open={showPointsEarnedModal} toggleModal={() => setShowPointsEarnedModal(false)} />
        <RewardsUnlockedModal open={showRewardsUnlockedModal} toggleModal={() => setShowRewardsUnlockedModal(false)} />
      </div>
    </>
  );
};

Content.Instructions = ({ order }: IContent): JSX.Element => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:OrderDetailsScreen' });
  const theme = useTheme();
  const { company } = useStore<REDUX_STORE_SLICES.INITIAL_DATA>(REDUX_STORE_SLICES.INITIAL_DATA);

  const getConfirmationEmail = (): string => {
    return `noreply@${company?.service_provider?.sender_email?.split('@')[1]}`;
  };

  return (
    <div className="mt-md p-md border rounded-lg">
      <div className="flex">
        <OrderPlacedIcon className="size-10 stroke-brand-primary" color={theme.color.brand.primary} />
        <div className="ml-md my-auto">
          <h6 className="text-gray-700 font-normal text-sm">
            {t('labels.order_number', {
              number: order.fulfillment.number,
            })}
          </h6>
          <h5 className="text-brand-primary">{t('text.thank_you', { name: order.customer_first_name })}</h5>
        </div>
      </div>
      <div className="bg-[#FCF4EB] p-md rounded-lg space-y-md mt-md">
        <p className="text-sm">
          {t('text.confirmation_email', {
            provider_email: getConfirmationEmail(),
          })}
        </p>
        <DeliveryTracker.DeliveryContent order={order} />
        <PickupInstruction order={order} className="text-sm" />
      </div>
      <DeliveryTracker order={order} className="mt-md w-full" />
      <div className="flex items-start mt-md">
        <p className="text-sm lg:w-fit max-w-1/2">{t('text.need_help')}</p>
        <a
          href={`tel:${company?.phone_number}`}
          className="ml-xs my-auto lg:block hidden text-sm text-brand-primary hover:underline"
        >
          {t('buttons.contact_us', { phone_number: company?.phone_number })}
        </a>
        <a
          href={`tel:${company?.phone_number}`}
          className="ml-xs lg:hidden block text-sm text-brand-primary hover:underline"
        >
          <span className="inline lg:block">{t('buttons.contact_us', { phone_number: '' })}</span>
          <span className="inline lg:block">{company?.phone_number}</span>
        </a>
      </div>
    </div>
  );
};

Content.CompanyDescription = (): JSX.Element => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:OrderDetailsScreen' });
  return (
    <div className="mt-md p-md border rounded-lg">
      <h6>{t('labels.restaurant')}</h6>
      <CompanyDescription shouldOpenMap className="mt-xs" />
    </div>
  );
};

Content.OrderSummary = ({ order }: IContent): JSX.Element => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:OrderDetailsScreen' });
  const { company } = useStore<REDUX_STORE_SLICES.INITIAL_DATA>(REDUX_STORE_SLICES.INITIAL_DATA);
  return (
    <div className="mt-md grid lg:grid-cols-2 gap-4">
      <div>
        <p className="mb-xs text-sm text-gray-700">{t('labels.contact')}</p>
        <p className="text-sm">
          {order.customer_first_name} {order.customer_last_name}
        </p>
        <p className="text-sm">{order.customer_phone_number}</p>
        <p className="text-sm">{order.customer_email}</p>
      </div>
      {order.total > 0 && (
        <div>
          <p className="mb-xs text-gray-700 text-sm">{t('labels.payment_method')}</p>
          <PaymentDetails
            payment_method_id={order.payment.payment_method_id}
            payment_method_brand={order.payment.payment_method_brand}
            payment_method_details={order.payment.payment_method_details}
            amount={order.total}
            classes={{
              text: 'text-sm',
            }}
          />
        </div>
      )}
      {order.method === ORDER_METHODS.DELIVERY && (
        <div>
          <p className="mb-xs text-sm text-gray-700">{t('labels.delivery')}</p>
          <p className="text-sm">
            {order.customer_first_name} {order.customer_last_name}
          </p>
          <p className="text-sm">
            {AddressHelpers.setCountry(order.delivery_country).format({
              street_name: order.delivery_street_name,
              street_number: order.delivery_street_number,
              city: order.delivery_city,
              state: order.delivery_state,
              zip_code: order.delivery_zip_code,
            })}
          </p>
        </div>
      )}
      {order.is_receipt_required && order.payment.payment_method_id !== PaymentMethod.Cash && (
        <div>
          <p className="mb-xs text-sm text-gray-700">{t('labels.billing_info')}</p>
          {Boolean(order.invoice_company_name) && <p className="text-sm">{order.invoice_company_name}</p>}
          {Boolean(order.invoice_tax_id) && <p className="text-sm">{order.invoice_tax_id}</p>}
          {Boolean(order.invoice_first_name) && Boolean(order.invoice_last_name) && (
            <p className="text-sm">
              {order.invoice_first_name} {order.invoice_last_name}
            </p>
          )}
          <p className="text-sm">
            {AddressHelpers.format({
              street_name: order.invoice_address,
              city: order.invoice_city,
              country: company?.country,
              zip_code: order.invoice_zip_code,
            })}
          </p>
        </div>
      )}
    </div>
  );
};

Content.PageLoading = ({ order, paymentStatus, children }: IContent & PropsWithChildren): JSX.Element => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:OrderDetailsScreen' });
  const { isMobileApp } = useMobileApp();

  if (paymentStatus === ORDER_PAYMENT_STATUSES.PENDING && !isMobileApp) {
    return <LoadingWithText description={t('loading.processing_payment')} />;
  }

  if (!order) {
    return <LoadingPage />;
  }

  return <>{children}</>;
};
